import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "react-hot-toast";
import authService from "@/services/authService";
import { useNavigate, useLocation } from "react-router-dom";

export default function ResetPassword({ isFirstTime = false, username }) {

  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const callFrom = queryParams.get("call");

  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [show, setShow] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log("Reset Password Page Loaded");
  }, []);

  const toggle = (key) => setShow((p) => ({ ...p, [key]: !p[key] }));

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleCancel = () => {
  if (window.history.length > 1) {
    navigate(-1);
  } else {
    navigate("/profile");
  }

  };

  const validate = () => {
    if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
      toast.error("All fields are required");
      return false;
    }

    if (form.newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return false;
    }

    if (form.newPassword !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      if (isFirstTime) {
        await authService.changePasswordFirstTime({
          username,
          oldPassword: form.oldPassword,
          newPassword: form.newPassword,
        });
      } else {
        await authService.changePassword({
          username,
          oldPassword: form.oldPassword,
          newPassword: form.newPassword,
        });
      }

      toast.success("Password updated successfully");

      setForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // 🔐 Auto logout after password change
      localStorage.clear();
      navigate("/login");

    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update password"
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch =
    form.confirmPassword.length > 0 &&
    form.newPassword === form.confirmPassword;

  return (
    <div className="flex justify-center py-10">
      <div className="w-full max-w-xl">

        {/* Logo only if opened from profile */}
        {callFrom === "profile" && (
          <img
            src="/loombiz-logo.png"
            alt="Loombiz"
            className="w-20 mx-auto mb-4"
          />
        )}

        {/* Header */}
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#FFF4E6] text-[#C45500]">
            <ShieldCheck size={18} />
          </div>

          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Reset Password
            </h2>

            <p className="text-sm text-slate-500">
              Update your password to keep your account secure
            </p>
          </div>
        </div>

        {isFirstTime && (
          <div className="mb-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle size={16} className="mt-0.5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Password change required
              </p>
              <p className="text-xs text-amber-700">
                This is your first login. Please update your password.
              </p>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-slate-200 bg-slate-50 p-6"
        >

          <div className="space-y-5">

            <PasswordField
              label="Current Password"
              name="oldPassword"
              value={form.oldPassword}
              show={show.old}
              toggle={() => toggle("old")}
              onChange={handleChange}
            />

            <PasswordField
              label="New Password"
              name="newPassword"
              value={form.newPassword}
              show={show.new}
              toggle={() => toggle("new")}
              onChange={handleChange}
            />

            <div>
              <PasswordField
                label="Confirm New Password"
                name="confirmPassword"
                value={form.confirmPassword}
                show={show.confirm}
                toggle={() => toggle("confirm")}
                onChange={handleChange}
              />

              {form.confirmPassword.length > 0 && (
                <p
                  className={`mt-1 text-xs ${
                    passwordsMatch ? "text-[#C45500]" : "text-red-500"
                  }`}
                >
                  {passwordsMatch
                    ? "Passwords match"
                    : "Passwords do not match"}
                </p>
              )}
            </div>

          </div>

          <div className="my-6 border-t border-slate-200" />

          <div className="flex justify-end gap-3">

            {/* Cancel button only when opened from profile */}
            {callFrom === "profile" && (
              <button
                type="button"
                onClick={handleCancel}
                className="px-5 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-[#232F3E] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#37475A] disabled:opacity-60"
            >
              {loading && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}

              Update Password
            </button>

          </div>

        </form>
      </div>
    </div>
  );
}

function PasswordField({ label, name, value, onChange, show, toggle }) {

  return (
    <div>

      <label className="mb-1.5 block text-base font-medium text-slate-700">
        {label}
      </label>

      <div className="relative group">

        <div
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2
          flex h-9 w-9 items-center justify-center
          rounded-md bg-[#FFF4E6] text-[#C45500]"
        >
          <Lock size={16} />
        </div>

        <input
          type={show ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          className="w-full rounded-lg border border-slate-300 bg-white
          py-3 pl-14 pr-12 text-base
          focus:border-[#f08804] focus:ring-1 focus:ring-[#f08804]"
        />

        <button
          type="button"
          onClick={toggle}
          className="absolute right-3 top-1/2 -translate-y-1/2
          flex h-9 w-9 items-center justify-center
          rounded-md text-slate-400 hover:bg-slate-100"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>

      </div>
    </div>
  );
}